/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"

import type { ApplicationTypeSDK } from "@/features/application-type/application-type-types"
import { ApplicationTypeViewModel } from "@/features/application-type/application-type-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { ClipboardValue } from "../components/clipboard-value"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"
import { ApplicationTypePicker } from "./application-type-picker"

// Use an ID that cannot represent a real app type as the "no selection" value.
const NO_APP_TYPE_ID = -1

export const ApplicationCredentials = observer(() => {
    const [appTypeId, setAppTypeId] = useState(NO_APP_TYPE_ID)
    const [sdkKeys, setSdkKeys] = useState<ApplicationTypeSDK | null>(null)

    const vm = useViewModel(ApplicationTypeViewModel)

    // Select the first app type if the user has not selected anything yet.
    useEffect(() => {
        if (vm.hasUserAppTypes && appTypeId === NO_APP_TYPE_ID) {
            setAppTypeId(vm.userAppTypes[0].id ?? NO_APP_TYPE_ID)
        }
    }, [appTypeId, vm.userAppTypes, vm.hasUserAppTypes])

    // SDK keys belong to the selected app type. Clear them immediately on change
    // and ignore a response from a previous selection.
    useEffect(() => {
        let cancelled = false
        setSdkKeys(null)

        void (async () => {
            if (appTypeId === NO_APP_TYPE_ID) return

            const result = await vm.getSDK(appTypeId)
            if (cancelled) return
            setSdkKeys(result.success ? result.data : null)
        })()

        return () => {
            cancelled = true
        }
    }, [appTypeId, vm])

    return (
        <Card className="gap-[calc(var(--card-spacing)*0.5)]">
            <CardHeader>
                <CardTitle>
                    <h2 className="m-0 text-xl font-bold uppercase">Application Credentials</h2>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <ApplicationTypePicker
                    items={vm.userAppTypes}
                    selectedId={appTypeId}
                    onSelect={(selectedId) => setAppTypeId(selectedId ?? NO_APP_TYPE_ID)}
                />

                <p className="m-0">Use the following credentials when configuring the service.</p>

                <dl className="grid grid-cols-[max-content_minmax(0,1fr)] items-center p-3 gap-x-2 gap-y-1 border bg-muted m-0">
                    <dt className="font-bold">Signature Public Key:</dt>
                    <dd className="m-0 font-mono">
                        <ClipboardValue value={sdkKeys?.signaturePublicKey} />
                    </dd>

                    <dt className="font-bold">Username:</dt>
                    <dd className="m-0 font-mono">
                        <ClipboardValue value={sdkKeys?.username} />
                    </dd>

                    <dt className="font-bold">Password:</dt>
                    <dd className="m-0 font-mono">
                        <ClipboardValue value={sdkKeys?.password} masked />
                    </dd>
                </dl>
            </CardContent>
        </Card>
    )
})
